import React, { useState } from "react";
import { List } from "immutable";
import PropTypes from 'prop-types';

import { getAllQuestionsFromCategories } from "mifid/utilities/mifidCryptoTestHelper";

import { CRYPTO_TEST_STATES, ANWSER_SELECTED_STATUS, ANWSER_NOT_SELECTED_STATUS } from "mifid/constants/constants";
const { TEST_IN_PROGRESS, TEST_SUMMARY, READ_DOCUMENTATION, SIGN_DOCUMENTATION } =
    CRYPTO_TEST_STATES;

export const useCryptoTest = ({
    immBrokerConvenienceMiFIDTest,
    updateAnswer,
    setCryptoTestIndex,
    setCryptoTestState,
    setCryptoTestLength,
    setEditionMode,
    closeCryptoResultModal,
    onMiFIDResultDisplayed,
    history,
}) => {
    const [allQuestions, setAllQuestions] = useState(List());
    const [testQuestion, setTestQuestion] = useState(List());
    const [previousAnseredQuestion, setPreviousAnseredQuestion] = useState(
        List()
    );
    const [unansweredQuestions, setUnansweredQuestions] = useState(List());
    const [questionsToEdit, setQuestionsToEdit] = useState(List());
    const [showToast, setShowToast] = useState(false);
    const [isModalDocumentationOpen, setIsModalDocumentationOpen] = useState(false);

    const testState = immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoTestState']);
    const currentQuestionIndex = immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoTestIndex']);
    const isEditing = immBrokerConvenienceMiFIDTest.getIn(["test", "isEditing"], false);

    const currentQuestionIsAwnsered = testState === TEST_IN_PROGRESS &&  (
        isEditing ||
            testQuestion.size > 0 && currentQuestionIndex >= 0 && currentQuestionIndex < testQuestion.size &&
            testQuestion.getIn([currentQuestionIndex, 'answer']) &&
            testQuestion.getIn([currentQuestionIndex, 'answer']).some(answer => answer.get('selected') === ANWSER_SELECTED_STATUS))

    const handleShowToast = () => {
        setShowToast(true);
    };

    const handleCloseToast = () => {
        setShowToast(false);
    };

    const goToTest = () => {
        setCryptoTestState(TEST_IN_PROGRESS);
        setCryptoTestIndex(0);
    };

    const goToTestSummary = () => {
        setCryptoTestState(TEST_SUMMARY);
    }

    const goToReadDocumentation = () => {
        setCryptoTestState(READ_DOCUMENTATION);
    };

    const onClickAnswer = (questionId, newValue) => {
        updateAnswer(0, questionId, newValue);
        const updateQuestionAnswers = (questions) => {
            return questions.map(question => {
            if (question.get('id') === questionId) {
                const updatedAnswers = question.get('answer').map(answer => {
                return answer.set('selected', answer.get('id') === newValue ? ANWSER_SELECTED_STATUS : ANWSER_NOT_SELECTED_STATUS);
                });
                return question.set('answer', updatedAnswers);
            }
            return question;
            });
        };
        const updateTestQuestions = () => {
            if (testQuestion.size > 0) {
            const questionIndex = testQuestion.findIndex(question => question.get('id') === questionId);
            if (questionIndex !== -1) {
                const question = testQuestion.get(questionIndex);
                const updatedAnswers = question.get('answer').map(answer => {
                return answer.set('selected', answer.get('id') === newValue ? ANWSER_SELECTED_STATUS : ANWSER_NOT_SELECTED_STATUS);
                });
                const updatedQuestion = question.set('answer', updatedAnswers);
                return testQuestion.set(questionIndex, updatedQuestion);
            }
            }
            return testQuestion;
        };

        const updatedTestQuestions = updateTestQuestions();
        const updatedAllQuestions = updateQuestionAnswers(allQuestions);
        const updatedPreviousAnsweredQuestions = updateQuestionAnswers(previousAnseredQuestion);
        const updatedUnansweredQuestions = updateQuestionAnswers(unansweredQuestions);

        setAllQuestions(updatedAllQuestions);
        setTestQuestion(updatedTestQuestions);
        setPreviousAnseredQuestion(updatedPreviousAnsweredQuestions);
        setUnansweredQuestions(updatedUnansweredQuestions);

        if (isEditing) {
            const updatedQuestionsToEdit = updateQuestionAnswers(questionsToEdit);
            setQuestionsToEdit(updatedQuestionsToEdit);
        }
    };

    const goToNextStep = () => {
        if (isEditing) {
            setEditionMode(false);
            handleShowToast(true);
            goToTestSummary();
        } else {
            if (currentQuestionIndex < testQuestion.size - 1) {
                setCryptoTestIndex(currentQuestionIndex + 1);
            } else {
                goToTestSummary();
            }
        }
    };

    const goToPreviousQuestion = () => {
        if (testState === TEST_SUMMARY) {
            setCryptoTestState(TEST_IN_PROGRESS);
            setCryptoTestIndex(testQuestion.size - 1);
        } else if (currentQuestionIndex > 0) {
            setCryptoTestIndex(currentQuestionIndex - 1);
        }
    };

    const buildTest = (immBrokerConvenienceMiFIDTest) => {
        const immTest = immBrokerConvenienceMiFIDTest.get("test");
        const immSchema = immTest.get("schema");
        const immCategories = immSchema.get("categories");
        const { allQuestions, answeredQuestions, unansweredQuestions } =
            getAllQuestionsFromCategories(immCategories);
        const takeTestWithAllQuestions = immTest.get('takeTestWithAllQuestions', false);
        setAllQuestions(allQuestions);
        if (takeTestWithAllQuestions) {
            setUnansweredQuestions(allQuestions);
            setTestQuestion(allQuestions);
            setCryptoTestLength(allQuestions.size);
        } else {
            setPreviousAnseredQuestion(answeredQuestions);
            setUnansweredQuestions(unansweredQuestions);
            setTestQuestion(unansweredQuestions);
            setCryptoTestLength(unansweredQuestions.size);
        }
        setCryptoTestState(TEST_IN_PROGRESS);
        setCryptoTestIndex(0);
    }

    const goToEditQuestion = (id) => {
        setEditionMode(true);
        const questionToEdit = allQuestions.find(question => question.get('id') === id);
        if (questionToEdit) {
            setQuestionsToEdit(List([questionToEdit]));
        }
        const questionInPreviousAnswered = previousAnseredQuestion.find(question => question.get('id') === id);

        if (questionInPreviousAnswered) {
            const updatedPreviousAnsweredQuestions = previousAnseredQuestion.filter(question => question.get('id') !== id);
            const updatedTestQuestions = testQuestion.push(questionToEdit).sortBy(question => question.get('subcategory'));
            const updatedUnansweredQuestions = unansweredQuestions.push(questionToEdit).sortBy(question => question.get('subcategory'));
            setPreviousAnseredQuestion(updatedPreviousAnsweredQuestions);
            setTestQuestion(updatedTestQuestions);
            setUnansweredQuestions(updatedUnansweredQuestions);
            setCryptoTestLength(updatedTestQuestions.size);
        }

        setCryptoTestIndex(0);
        setCryptoTestState(TEST_IN_PROGRESS);

    }

    const goToPurchaseFlow = () => {
        const continueUrl = immBrokerConvenienceMiFIDTest.getIn(['urlContinueOperation', 0]);
        onMiFIDResultDisplayed();
        history.push(continueUrl);
    }

    const handleModalClick = () => {
        closeCryptoResultModal();
        if (immBrokerConvenienceMiFIDTest.getIn(['test', 'documentationHasSigned'])) {
            goToPurchaseFlow();
        } else {
            goToReadDocumentation();
        }
    }

    const openModalDocumentation = () => {
        setIsModalDocumentationOpen(true);
    }

    const closeModalDocumentation = () => {
        setIsModalDocumentationOpen(false);
    };

    const goToSignDocumentation = () => {
        setCryptoTestState(SIGN_DOCUMENTATION);
        setIsModalDocumentationOpen(false);
    }


    return {
        testState,
        allQuestions,
        testQuestion,
        previousAnseredQuestion,
        unansweredQuestions,
        currentQuestionIndex,
        questionsToEdit,
        isEditing,
        showToast,
        isModalDocumentationOpen,
        currentQuestionIsAwnsered,
        buildTest,
        goToTestSummary,
        goToReadDocumentation,
        onClickAnswer,
        goToPreviousQuestion,
        goToEditQuestion,
        handleShowToast,
        handleCloseToast,
        handleModalClick,
        goToPurchaseFlow,
        openModalDocumentation,
        closeModalDocumentation,
        goToPurchaseFlow,
        goToNextStep,
        goToSignDocumentation,
    };
};

useCryptoTest.propTypes = {
    immBrokerConvenienceMiFIDTest: PropTypes.object.isRequired,
    updateAnswer: PropTypes.func.isRequired,
    setCryptoTestIndex: PropTypes.func.isRequired,
    setCryptoTestState: PropTypes.func.isRequired,
    setCryptoTestLength: PropTypes.func.isRequired,
    setEditionMode: PropTypes.func.isRequired,
    closeCryptoResultModal: PropTypes.func.isRequired,
    onMiFIDResultDisplayed: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
};
